import * as Sentry from "@sentry/react";
import * as sessionsApi from "@/api/sessions";
import * as routesApi from "@/api/routes";
import { getCurrentUserId, getSignInGeneration } from "./currentUser";
import {
  deletePendingRoute,
  deletePendingSession,
  listPendingRoutes,
  listPendingRoutesForSession,
  listPendingSessions,
  putPendingRoute,
  putPendingSession,
  revokeObjectUrl,
} from "./db";
import { isNetworkError, isOnline, onNetworkChange } from "./network";
import { clearRouteSyncStatus, clearSessionSyncStatus } from "./statusCache";
import type { PendingRoute, PendingSession } from "./types";
import { withoutNulls } from "./utils";

export type SyncStatus = "idle" | "syncing" | "offline";

export interface SyncState {
  status: SyncStatus;
  pendingCount: number;
  failedCount: number;
}

let state: SyncState = { status: "idle", pendingCount: 0, failedCount: 0 };
const listeners = new Set<(state: SyncState) => void>();
const idRemapListeners = new Set<(oldId: number, newId: number) => void>();
let running = false;
let networkUnsubscribe: (() => void) | null = null;

function setState(patch: Partial<SyncState>): void {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener(state));
}

export function subscribeSyncStatus(cb: (state: SyncState) => void): () => void {
  listeners.add(cb);
  cb(state);
  return () => {
    listeners.delete(cb);
  };
}

// Called by sessionsRepo/routesRepo after any local create/edit/delete, since
// those happen outside a sync run and wouldn't otherwise update the banner's
// counts — or its offline/idle status — until the next reconnect.
export async function refreshPendingCounts(): Promise<void> {
  if (running) return; // a sync run's own `finally` block already sets state
  const [counts, online] = await Promise.all([computeCounts(), isOnline()]);
  setState({ ...counts, status: online ? "idle" : "offline" });
}

/** How many of the current user's items haven't reached the server, failed ones included. */
export async function countUnsynced(): Promise<number> {
  const { pendingCount, failedCount } = await computeCounts();
  return pendingCount + failedCount;
}

// Fired once a pending session's create POST succeeds, so a page currently
// showing /sessions/<oldNegativeId> can navigate to the real id in place.
export function subscribeIdRemap(cb: (oldId: number, newId: number) => void): () => void {
  idRemapListeners.add(cb);
  return () => {
    idRemapListeners.delete(cb);
  };
}

async function computeCounts(): Promise<{ pendingCount: number; failedCount: number }> {
  const userId = getCurrentUserId();
  const [sessions, routes] = await Promise.all([listPendingSessions(), listPendingRoutes()]);
  const mine = [
    ...sessions.filter((s) => s.createdByUserId === userId),
    ...routes.filter((r) => r.createdByUserId === userId),
  ];
  return {
    pendingCount: mine.filter((item) => item.status !== "failed").length,
    failedCount: mine.filter((item) => item.status === "failed").length,
  };
}

function byCreatedAt(a: { createdAt: string }, b: { createdAt: string }): number {
  return a.createdAt.localeCompare(b.createdAt);
}

function serverErrorMessage(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === "string" && detail ? detail : "Couldn't sync — please review and try again.";
}

// A sync run can outlive the session that started it: logout clears the
// user's queue while a request is still in flight. Check before every write,
// so the run never puts a cleared record back on the device.
function signedOutSince(generation: number): boolean {
  return getSignInGeneration() !== generation;
}

// Returns false to mean "stop this run": still offline (retry on the next
// reconnect) or the user signed out — as opposed to a server rejection (4xx), which is marked
// `failed` on that one item and does not stop the rest of the queue.
async function syncRoute(route: PendingRoute, serverSessionId: number, generation: number): Promise<boolean> {
  try {
    const created = await routesApi.logRoute(serverSessionId, withoutNulls(route.data));
    if (route.photoBlob) {
      try {
        await routesApi.uploadRoutePhoto(serverSessionId, created.id, route.photoBlob);
      } catch (err) {
        // Best-effort, mirrors LogRoutePage's own online photo-upload handling.
        Sentry.captureException(err);
      }
    }
    revokeObjectUrl(route.id);
    clearRouteSyncStatus(route.id);
    await deletePendingRoute(route.id);
    return true;
  } catch (err) {
    if (isNetworkError(err) || signedOutSince(generation)) return false;
    await putPendingRoute({ ...route, status: "failed", error: serverErrorMessage(err) });
    return true;
  }
}

async function syncSession(session: PendingSession, generation: number): Promise<boolean> {
  let serverId = session.serverId;

  if (serverId == null) {
    try {
      const created = await sessionsApi.createSession(withoutNulls(session.data));
      serverId = created.id;
    } catch (err) {
      if (isNetworkError(err) || signedOutSince(generation)) return false;
      await putPendingSession({ ...session, status: "failed", error: serverErrorMessage(err) });
      return true;
    }

    if (signedOutSince(generation)) return false;
    await putPendingSession({ ...session, serverId });
    // Re-point every child route at the real session id before syncing any
    // of them, so a route that fails validation still ends up correctly
    // associated with the now-real session rather than a bookkeeping id
    // that's about to be deleted.
    const children = await listPendingRoutesForSession(session.id);
    if (signedOutSince(generation)) return false;
    await Promise.all(children.map((route) => putPendingRoute({ ...route, sessionId: serverId! })));
    idRemapListeners.forEach((listener) => listener(session.id, serverId!));
  }

  const routes = (await listPendingRoutesForSession(serverId)).sort(byCreatedAt);
  for (const route of routes) {
    const ok = await syncRoute(route, serverId, generation);
    if (!ok) return false;
  }

  clearSessionSyncStatus(session.id);
  await deletePendingSession(session.id);
  return true;
}

export async function runSync(): Promise<void> {
  if (running) return;

  const userId = getCurrentUserId();
  const generation = getSignInGeneration();
  if (userId == null) {
    setState({ status: "idle", ...(await computeCounts()) });
    return;
  }

  if (!(await isOnline())) {
    setState({ status: "offline", ...(await computeCounts()) });
    return;
  }

  running = true;
  setState({ status: "syncing" });
  try {
    const sessions = (await listPendingSessions())
      .filter((s) => s.createdByUserId === userId && s.status !== "failed")
      .sort(byCreatedAt);

    for (const session of sessions) {
      const ok = await syncSession(session, generation);
      if (!ok) return; // offline or signed out mid-run — retry on next reconnect
    }

    // Routes logged offline directly against an already-synced session.
    const orphanRoutes = (await listPendingRoutes())
      .filter((r) => r.createdByUserId === userId && r.status !== "failed" && r.sessionId > 0)
      .sort(byCreatedAt);

    for (const route of orphanRoutes) {
      const ok = await syncRoute(route, route.sessionId, generation);
      if (!ok) return;
    }
  } finally {
    running = false;
    const online = await isOnline();
    setState({ status: online ? "idle" : "offline", ...(await computeCounts()) });
  }
}

export function triggerSync(): Promise<void> {
  return runSync();
}

export function startSyncEngine(): () => void {
  void computeCounts().then((counts) => setState(counts));
  void runSync();
  networkUnsubscribe = onNetworkChange((online) => {
    if (online) void runSync();
  });
  return () => {
    networkUnsubscribe?.();
    networkUnsubscribe = null;
  };
}
